import { Link } from "react-router-dom";
import { ArrowLeft, Shield, Lock, FileText, Info } from "lucide-react";

const LOGO_URL = "https://customer-assets.emergentagent.com/job_opport-biz-ma/artifacts/oknd9jsa_Logo%20Club%20Business%20Maroc%20Annuaire.png";

export default function LegalPage() {
  return (
    <div
      className="min-h-screen"
      style={{ background: "linear-gradient(135deg, #081026 0%, #0D1B3E 50%, #081530 100%)" }}
    >
      {/* Decorative background */}
      <div className="absolute inset-0 pointer-events-none opacity-5" style={{
        backgroundImage: "radial-gradient(circle, #C9A227 1px, transparent 1px)",
        backgroundSize: "40px 40px",
      }} />

      <div className="relative z-10 max-w-4xl mx-auto px-4 py-8 sm:py-12">
        {/* Header */}
        <div className="flex flex-col items-center mb-8">
          <img src={LOGO_URL} alt="Club Business Maroc" className="w-20 h-20 object-contain rounded-xl mb-4" />
          <h1 className="font-outfit font-bold text-white text-3xl sm:text-4xl text-center mb-2">
            Conditions & Confidentialité
          </h1>
          <p className="text-cbm-muted text-sm text-center">
            Club Business Maroc — Plateforme privée B2B
          </p>
        </div>

        {/* Content card */}
        <div className="cbm-glass rounded-2xl p-6 sm:p-8 shadow-card mb-6">
          {/* Navigation interne */}
          <div className="flex flex-wrap gap-2 mb-8 pb-6 border-b cbm-divider">
            <a href="#cgu" className="text-xs text-cbm-muted hover:text-gold transition-colors flex items-center gap-1.5">
              <FileText size={14} /> CGU
            </a>
            <span className="text-cbm-muted">•</span>
            <a href="#confidentialite" className="text-xs text-cbm-muted hover:text-gold transition-colors flex items-center gap-1.5">
              <Lock size={14} /> Confidentialité
            </a>
            <span className="text-cbm-muted">•</span>
            <a href="#mentions" className="text-xs text-cbm-muted hover:text-gold transition-colors flex items-center gap-1.5">
              <Info size={14} /> Mentions légales
            </a>
            <span className="text-cbm-muted">•</span>
            <a href="#donnees" className="text-xs text-cbm-muted hover:text-gold transition-colors flex items-center gap-1.5">
              <Shield size={14} /> Données personnelles
            </a>
          </div>

          {/* Section 1 : Conditions Générales d'Utilisation */}
          <section id="cgu" className="mb-10 scroll-mt-6">
            <h2 className="flex items-center gap-2 font-outfit font-semibold text-gold text-xl mb-4">
              <FileText size={20} /> Conditions Générales d'Utilisation
            </h2>
            <div className="space-y-4 text-cbm-secondary text-sm leading-relaxed">
              <p>
                <strong className="text-white">1.1 Objet</strong><br />
                Le Club Business Maroc est une plateforme privée réservée aux entrepreneurs et dirigeants marocains. L'accès est strictement sur invitation. En créant un compte, vous acceptez de respecter les présentes conditions.
              </p>
              <p>
                <strong className="text-white">1.2 Inscription et compte</strong><br />
                Chaque membre est responsable de la confidentialité de ses identifiants. Vous vous engagez à fournir des informations exactes et à maintenir votre profil à jour (minimum 80% de complétion pour apparaître dans l'annuaire public).
              </p>
              <p>
                <strong className="text-white">1.3 Utilisation de la plateforme</strong><br />
                Vous vous engagez à utiliser la plateforme de manière professionnelle et respectueuse. Tout comportement abusif, spamming, ou tentative de scraping est strictement interdit et peut entraîner la suspension immédiate de votre compte.
              </p>
              <p>
                <strong className="text-white">1.4 Contenu publié</strong><br />
                Vous conservez la propriété de vos contenus (annonces, avantages, avis). En les publiant, vous accordez au Club Business Maroc une licence non-exclusive pour les afficher et les partager au sein de la communauté.
              </p>
              <p>
                <strong className="text-white">1.5 Quotas et limites</strong><br />
                Des quotas s'appliquent (4 annonces/mois, 10 demandes de contact/mois). L'administrateur se réserve le droit de modifier ces limites.
              </p>
              <p>
                <strong className="text-white">1.6 Résiliation</strong><br />
                Vous pouvez supprimer votre compte à tout moment en contactant l'administrateur. Nous nous réservons le droit de suspendre ou supprimer tout compte en cas de non-respect des présentes conditions.
              </p>
            </div>
          </section>

          {/* Section 2 : Politique de Confidentialité */}
          <section id="confidentialite" className="mb-10 scroll-mt-6">
            <h2 className="flex items-center gap-2 font-outfit font-semibold text-gold text-xl mb-4">
              <Lock size={20} /> Politique de Confidentialité
            </h2>
            <div className="space-y-4 text-cbm-secondary text-sm leading-relaxed">
              <p>
                <strong className="text-white">2.1 Données collectées</strong><br />
                Nous collectons les informations que vous fournissez lors de votre inscription et via votre profil : nom, prénom, email, entreprise, fonction, secteur, ville, coordonnées professionnelles, photo de profil, etc.
              </p>
              <p>
                <strong className="text-white">2.2 Utilisation des données</strong><br />
                Vos données sont utilisées pour : gérer votre compte, afficher votre profil dans l'annuaire (si complet à 80%), faciliter les mises en relation, vous envoyer des notifications pertinentes, et améliorer la plateforme.
              </p>
              <p>
                <strong className="text-white">2.3 Partage des données</strong><br />
                Vos informations de profil (nom, entreprise, secteur, ville, coordonnées si visibles) sont partagées au sein de la communauté privée du Club Business Maroc. Nous ne vendons ni ne partageons vos données avec des tiers externes sans votre consentement explicite.
              </p>
              <p>
                <strong className="text-white">2.4 Sécurité</strong><br />
                Nous mettons en œuvre des mesures de sécurité techniques et organisationnelles pour protéger vos données contre tout accès non autorisé, perte ou altération. Les mots de passe sont chiffrés (bcrypt) et les communications sont sécurisées (HTTPS).
              </p>
              <p>
                <strong className="text-white">2.5 Cookies et trackers</strong><br />
                Nous utilisons des cookies de session pour maintenir votre connexion. Nous n'utilisons pas de cookies publicitaires tiers.
              </p>
            </div>
          </section>

          {/* Section 3 : Mentions Légales */}
          <section id="mentions" className="mb-10 scroll-mt-6">
            <h2 className="flex items-center gap-2 font-outfit font-semibold text-gold text-xl mb-4">
              <Info size={20} /> Mentions Légales
            </h2>
            <div className="space-y-4 text-cbm-secondary text-sm leading-relaxed">
              <p>
                <strong className="text-white">Éditeur de la plateforme</strong><br />
                Club Business Maroc<br />
                Email de contact : contact@club-business-maroc.com
              </p>
              <p>
                <strong className="text-white">Hébergement</strong><br />
                La plateforme est hébergée sur une infrastructure cloud sécurisée.<br />
                Kubernetes / Cloud Provider infrastructure
              </p>
              <p>
                <strong className="text-white">Directeur de publication</strong><br />
                Administrateur du Club Business Maroc
              </p>
              <p>
                <strong className="text-white">Propriété intellectuelle</strong><br />
                Tous les éléments de la plateforme (design, logo, marque, code) sont la propriété exclusive du Club Business Maroc. Toute reproduction ou utilisation non autorisée est interdite.
              </p>
            </div>
          </section>

          {/* Section 4 : Protection des Données Personnelles (RGPD) */}
          <section id="donnees" className="scroll-mt-6">
            <h2 className="flex items-center gap-2 font-outfit font-semibold text-gold text-xl mb-4">
              <Shield size={20} /> Protection des Données Personnelles
            </h2>
            <div className="space-y-4 text-cbm-secondary text-sm leading-relaxed">
              <p>
                <strong className="text-white">4.1 Responsable du traitement</strong><br />
                Club Business Maroc est responsable du traitement de vos données personnelles. Pour toute question, contactez : contact@club-business-maroc.com
              </p>
              <p>
                <strong className="text-white">4.2 Vos droits</strong><br />
                Conformément au RGPD et à la loi marocaine sur la protection des données, vous disposez des droits suivants :
              </p>
              <ul className="list-disc list-inside space-y-1 ml-4">
                <li><strong className="text-white">Droit d'accès</strong> : obtenir une copie de vos données</li>
                <li><strong className="text-white">Droit de rectification</strong> : corriger vos informations</li>
                <li><strong className="text-white">Droit à l'effacement</strong> : demander la suppression de votre compte</li>
                <li><strong className="text-white">Droit d'opposition</strong> : vous opposer à certains traitements</li>
                <li><strong className="text-white">Droit à la portabilité</strong> : récupérer vos données dans un format structuré</li>
              </ul>
              <p>
                Pour exercer ces droits, contactez-nous à : contact@club-business-maroc.com
              </p>
              <p>
                <strong className="text-white">4.3 Conservation des données</strong><br />
                Vos données sont conservées pendant toute la durée de votre adhésion au Club, et jusqu'à 2 ans après la fermeture de votre compte pour des raisons légales et de traçabilité.
              </p>
              <p>
                <strong className="text-white">4.4 Transfert de données</strong><br />
                Vos données sont hébergées au sein de l'Union Européenne ou dans des pays offrant un niveau de protection équivalent.
              </p>
            </div>
          </section>

          <div className="mt-8 pt-6 border-t cbm-divider text-center">
            <p className="text-cbm-muted text-xs">
              Dernière mise à jour : Janvier 2025 • Version v1.0
            </p>
          </div>
        </div>

        {/* Bouton retour */}
        <div className="text-center">
          <Link
            to="/login"
            className="inline-flex items-center gap-2 text-sm text-cbm-muted hover:text-gold transition-colors"
          >
            <ArrowLeft size={16} />
            Retour à la connexion
          </Link>
        </div>
      </div>
    </div>
  );
}
